import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/homeData';

export default function BasetaoShippingUSA() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">USA Shipping</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao Shipping to USA: Rates, Methods & Timeline</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">A complete guide for USA customers covering BaseTao shipping rates, delivery methods, and estimated timelines.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">BaseTao Shipping Options to the USA</h2>
              <p className="text-foreground-600 leading-relaxed mb-4"><strong className="text-foreground-900">BaseTao shipping to USA</strong> offers several carrier options, each with different trade-offs between speed and cost. The main shipping methods available for USA destinations include express carriers with faster delivery and economy options that take longer but cost significantly less.</p>
              <p className="text-foreground-600 leading-relaxed">Understanding these options helps you choose the right balance for your specific haul — whether you are in a hurry for a limited release item or building a larger shipment where saving on shipping is the priority.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Estimated Shipping Rates by Weight</h2>
              <div className="overflow-hidden rounded-lg border border-background-200/70">
                <table className="w-full text-sm">
                  <thead><tr className="bg-background-100"><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Package Weight</th><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Economy (Est.)</th><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Standard (Est.)</th><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Express (Est.)</th></tr></thead>
                  <tbody className="divide-y divide-background-200/60">
                    {[{w:'Up to 1 kg',e:'$12-$18',s:'$20-$28',x:'$30-$40'},{w:'1-3 kg',e:'$18-$30',s:'$28-$45',x:'$40-$60'},{w:'3-5 kg',e:'$30-$50',s:'$45-$70',x:'$60-$90'},{w:'5-10 kg',e:'$50-$80',s:'$70-$110',x:'$90-$140'},{w:'10+ kg',e:'$80-$140',s:'$110-$180',x:'$140-$220'}].map((r,i)=>(<tr key={i}><td className="px-4 py-2.5 text-foreground-700 font-medium text-xs">{r.w}</td><td className="px-4 py-2.5 text-foreground-500 text-xs">{r.e}</td><td className="px-4 py-2.5 text-foreground-500 text-xs">{r.s}</td><td className="px-4 py-2.5 text-foreground-500 text-xs">{r.x}</td></tr>))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-foreground-400 mt-3">Note: These are estimated ranges based on publicly available information. Actual rates vary based on current carrier pricing, fuel surcharges, and seasonal demand. Always check current rates before shipping.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Tips for USA Shoppers</h2>
              <ul className="space-y-3 text-foreground-600">
                {['Consolidate multiple items into one shipment to reduce per-item shipping cost','Be aware of US customs de minimis thresholds when planning larger hauls','Use your BaseTao Spreadsheet to compare shipping estimates across different methods','Consider seasonal timing — shipping during major holiday periods may experience delays','Track your packages using the carrier tracking number provided after shipment'].map((item,i)=>(
                  <li key={i} className="flex items-start gap-2"><div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-check-line text-primary-500 text-sm"></i></div><span className="text-sm">{item}</span></li>
                ))}
              </ul>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Calculate Your USA Shipping</h3>
              <p className="text-foreground-600 text-sm mb-4">Use your BaseTao Spreadsheet with our shipping calculator methodology to estimate costs.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">Browse Products <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-shipping-cost/" className="hover:text-primary-600 transition-colors">Shipping Cost Guide</Link>
              <Link to="/basetao-shipping-calculator/" className="hover:text-primary-600 transition-colors">Shipping Calculator</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Shipping to USA: Rates, Methods & Timeline',
            description: 'A complete guide for USA customers covering BaseTao shipping rates, delivery methods, and estimated timelines.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-shipping-usa/',
            },
          }),
        }}
      />
    </div>
  );
}